import type { ReactElement } from "react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import {
  DashboardSignalPanel,
  SignalRow,
} from "@/modules/dashboard/components/redesign/DashboardSignalPanel";
import type { DashboardPaymentHealthPanelProps } from "@/modules/dashboard/types/dashboard-analytics.types";

/** Above this failure share the meter switches from warning to critical. */
const CRITICAL_FAILED_RATE = 0.2;

/**
 * Online payment health.
 *
 * `attempts === 0` is the normal state for most stores right now, so it gets a
 * real empty state: nobody has paid online yet. Rendering "0%" beside a meter
 * would claim a perfect gateway on the strength of no evidence at all.
 */
export function DashboardPaymentHealthPanel({
  health,
  formatValue,
  formatPercent,
  formatCount,
}: DashboardPaymentHealthPanelProps): ReactElement {
  const t = useTranslations();
  const isEmpty = health.attempts === 0;
  const critical = health.failedRate >= CRITICAL_FAILED_RATE;
  const meterColor = critical ? "var(--error-500)" : "var(--warning-500)";

  // The overall meter's readout, in one string used as BOTH the hover title
  // and the bar's accessible name — a `title` alone is mouse-only, and this
  // panel's whole point is a money figure that means nothing without the
  // attempt counts beside it.
  const meterSummary = t(keys.dashboard.barSummary, {
    label: t(keys.dashboard.payTitle),
    amount: formatValue(health.failedAmount),
    orders: t(keys.dashboard.payProviderValue, {
      failed: health.failed,
      attempts: formatCount(health.attempts),
    }),
  });

  return (
    <DashboardSignalPanel
      title={t(keys.dashboard.payTitle)}
      subtitle={t(keys.dashboard.paySubtitle, {
        attempts: formatCount(health.attempts),
      })}
      isEmpty={isEmpty}
      emptyTitle={t(keys.dashboard.payEmptyTitle)}
      emptyBody={t(keys.dashboard.payEmptyBody)}
    >
      <div className="flex items-baseline gap-2">
        <span className="text-[22px] font-bold leading-none text-[color:var(--text-primary)]">
          <bdi>{formatPercent(health.failedRate)}</bdi>
        </span>
        <span className="text-[11.5px] text-[color:var(--text-muted)]">
          {t(keys.dashboard.payFailedRate)}
        </span>
      </div>

      {/* Meter: the fill carries severity, the track is a quiet step of the
          same surface. The number above states the value, so the bar is a
          supporting encoding rather than the only one. */}
      <div
        role="img"
        aria-label={meterSummary}
        title={meterSummary}
        className="mt-2 h-[6px] w-full overflow-hidden rounded-full bg-[color:var(--track)]"
      >
        <div
          aria-hidden="true"
          className="h-full rounded-full"
          style={{
            width: `${Math.min(100, Math.max(2, health.failedRate * 100))}%`,
            background: meterColor,
          }}
        />
      </div>

      <div className="mt-3">
        <SignalRow
          label={t(keys.dashboard.payAttempts)}
          value={health.attempts.toLocaleString()}
        />
        <SignalRow
          label={t(keys.dashboard.payFailedCount)}
          value={health.failed.toLocaleString()}
        />
        {/* The one money figure in the panel — it states which failed attempts
            it came out of rather than leaving the reader to pair it with the
            row above. */}
        <SignalRow
          label={t(keys.dashboard.payFailedAmount)}
          value={formatValue(health.failedAmount)}
          meta={t(keys.dashboard.payProviderValue, {
            failed: health.failed,
            attempts: formatCount(health.attempts),
          })}
          strong
        />
      </div>

      {health.byProvider.length > 0 ? (
        <div className="mt-3 border-t border-[color:var(--line)] pt-2">
          <p className="mb-1 text-[10.5px] font-semibold uppercase tracking-[0.03em] text-[color:var(--text-muted)]">
            {t(keys.dashboard.payByProvider)}
          </p>
          <ul className="flex flex-col gap-2.5">
            {health.byProvider.map((provider) => {
              const counts = t(keys.dashboard.payProviderValue, {
                failed: provider.failed,
                attempts: provider.attempts,
              });
              const summary = t(keys.dashboard.barSummary, {
                label: provider.provider,
                amount: formatValue(provider.failedAmount),
                orders: counts,
              });
              // Share of THIS provider's own attempts, not of the panel total:
              // the question a per-gateway row answers is "how often does this
              // one fail", which a total-relative bar would not show.
              const failedShare =
                provider.attempts > 0
                  ? (provider.failed / provider.attempts) * 100
                  : 0;
              return (
                <li key={provider.provider} className="min-w-0">
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="min-w-0 truncate text-[11.5px] text-[color:var(--text-secondary)]">
                      {provider.provider}
                    </span>
                    <span className="shrink-0 font-mono text-[11.5px] font-semibold tabular-nums text-[color:var(--text-primary)]">
                      <bdi>{formatValue(provider.failedAmount)}</bdi>
                    </span>
                  </div>
                  <div
                    role="img"
                    aria-label={summary}
                    title={summary}
                    className="mt-1 h-[5px] w-full overflow-hidden rounded-full bg-[color:var(--track)]"
                  >
                    <div
                      aria-hidden="true"
                      className="h-full rounded-full"
                      style={{
                        width: `${Math.min(100, Math.max(2, failedShare))}%`,
                        background: meterColor,
                      }}
                    />
                  </div>
                  {/* Both counts in text: the money above is meaningless
                      without knowing it came out of how many attempts. */}
                  <p className="mt-0.5 text-[10.5px] text-[color:var(--text-muted)]">
                    <bdi>{counts}</bdi>
                  </p>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </DashboardSignalPanel>
  );
}
